import { z } from 'zod';
import { addressSchema, blockTagSchema, supportedChainSlugSchema } from './common.js';
import { logEntrySchema } from './logs.js';
/** One ABI parameter description stored on a marketplace method. */
export const marketplaceMethodParamSchema = z
    .object({
    name: z.string().min(1),
    type: z.string().min(1),
    kind: z.enum(['input', 'output']),
    indexed: z.boolean().optional(),
    description: z.string().nullable().optional(),
})
    .strict();
export const marketplaceContractMethodSchema = z
    .object({
    method: z.string().min(1),
    abiName: z.string().min(1),
    abiType: z.enum(['function', 'event']),
    description: z.string().nullable().optional(),
    params: z.array(marketplaceMethodParamSchema),
})
    .strict();
/**
 * One deployment of a marketplace app: a `name` (unique per chain within the
 * app, or unique within a template app) plus optional chain + address + its own
 * ABI and selected methods.
 *
 * Concrete deployments bake chain and deployment name into published JSON-RPC
 * method names so callers do not pass chain/address.
 *
 * Template deployments omit chain/address; published names omit the chain
 * segment, and callers must supply `chain` + `address` in the call body.
 */
export const marketplaceContractDeploymentSchema = z
    .object({
    name: z.string().min(1),
    chain: z.string().nullable(),
    address: addressSchema.nullable(),
    abi: z.unknown(),
    methods: z.array(marketplaceContractMethodSchema),
})
    .strict();
/** @deprecated Use marketplaceContractDeploymentSchema. */
export const marketplaceContractChainSchema = marketplaceContractDeploymentSchema;
/**
 * Published marketplace method metadata (discovery). Each entry is uniquely
 * callable by `method` alone. For concrete deployments, `chain` / `address` /
 * `deploymentName` describe the target. For templates (`isTemplate: true`),
 * `chain` / `address` are null and must be supplied in the call body.
 */
export const marketplaceMethodEntrySchema = z
    .object({
    method: z.string().min(1),
    abiName: z.string().min(1),
    abiType: z.enum(['function', 'event']),
    description: z.string().nullable().optional(),
    params: z.array(marketplaceMethodParamSchema),
    appId: z.string().min(1),
    contractName: z.string().min(1),
    deploymentName: z.string().min(1),
    isTemplate: z.boolean(),
    chain: z.string().min(1).nullable(),
    address: addressSchema.nullable(),
})
    .strict();
export const listMarketplaceMethodsParamsSchema = z
    .object({
    chain: z.string().min(1).optional(),
    appId: z.string().min(1).optional(),
    abiType: z.enum(['function', 'event']).optional(),
})
    .strict();
export const listMarketplaceMethodsDataSchema = z
    .object({
    methods: z.array(marketplaceMethodEntrySchema),
})
    .strict();
export const getMarketplaceContractParamsSchema = z
    .object({
    appId: z.string().min(1),
})
    .strict();
export const marketplaceContractDataSchema = z
    .object({
    appId: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    isTemplate: z.boolean(),
    deployments: z.array(marketplaceContractDeploymentSchema),
})
    .strict();
/**
 * Operation segment in published marketplace method names. Encodes how the
 * ABI item is invoked so reads, simulations, log queries, and future writes
 * stay distinct:
 * - `call` — view/pure function via `eth_call`
 * - `simulateCall` — non-view function via `eth_simulateV1` (nothing broadcast)
 * - `getLogs` — event via `eth_getLogs`
 * - `write` — reserved for a future on-chain send of the same function
 */
export const MARKETPLACE_METHOD_OPERATION_CALL = 'call';
export const MARKETPLACE_METHOD_OPERATION_SIMULATE_CALL = 'simulateCall';
export const MARKETPLACE_METHOD_OPERATION_GET_LOGS = 'getLogs';
export const MARKETPLACE_METHOD_OPERATION_WRITE = 'write';
export const MARKETPLACE_METHOD_OPERATIONS = [
    MARKETPLACE_METHOD_OPERATION_CALL,
    MARKETPLACE_METHOD_OPERATION_SIMULATE_CALL,
    MARKETPLACE_METHOD_OPERATION_GET_LOGS,
    MARKETPLACE_METHOD_OPERATION_WRITE,
];
/**
 * A published marketplace method is called by its own JSON-RPC method name.
 * App ids, chain slugs, and deployment names are alphanumeric (no `_`); the
 * operation segment is `call` | `simulateCall` | `getLogs` | `write`; ABI names
 * may include `_`. Template apps omit the chain segment.
 *
 * Concrete: `spectrum_<appId>_<chain>_<deploymentName>_<operation>_<abiName>`
 * Template: `spectrum_<appId>_<deploymentName>_<operation>_<abiName>`
 */
export const MARKETPLACE_METHOD_NAME_PATTERN = /^spectrum_[0-9a-zA-Z]+(_[0-9a-zA-Z]+)?_[0-9a-zA-Z]+_(call|simulateCall|getLogs|write)_[A-Za-z_$][A-Za-z0-9_$]*$/;
/** Extracts the operation segment from a published marketplace method name. */
export const MARKETPLACE_METHOD_OPERATION_PATTERN = /_(call|simulateCall|getLogs|write)_([A-Za-z_$][A-Za-z0-9_$]*)$/;
export const marketplaceMethodNameSchema = z
    .string()
    .regex(MARKETPLACE_METHOD_NAME_PATTERN, 'Expected a method name of the form spectrum_<appId>_[<chain>_]<deploymentName>_<operation>_<abiName>');
/**
 * Block height for marketplace function calls/simulations: omit or `"latest"`
 * for the tip, otherwise a block number (decimal number or decimal string).
 */
export const marketplaceHeightSchema = z.union([
    z.literal('latest'),
    z.number().int().nonnegative(),
    z.string().regex(/^\d+$/, 'Expected a decimal block number'),
]);
/**
 * Params for
 * `spectrum_<appId>_[<chain>_]<deploymentName>_<operation>_<abiName>`.
 *
 * Concrete deployments resolve chain/address from the method name — do not pass
 * them here. Template methods (no chain segment) require `chain` and `address`.
 *
 * `…_call_…` view/pure functions run as `eth_call` (`args` + optional `height`).
 * `…_simulateCall_…` non-view functions run as `eth_simulateV1` (`args` +
 * optional `height` / `from` / `value`).
 * `…_getLogs_…` events use `args` as positional topic filters for indexed
 * inputs, plus the same `fromBlock`/`toBlock`/`cursor` windowing as `getLogs`.
 */
export const marketplaceMethodCallParamsSchema = z
    .object({
    args: z.array(z.unknown()).default([]),
    chain: supportedChainSlugSchema.or(z.string().min(1)).optional(),
    address: addressSchema.optional(),
    from: addressSchema.optional(),
    value: z.string().min(1).optional(),
    height: marketplaceHeightSchema.optional(),
    cursor: blockTagSchema.optional(),
    fromBlock: blockTagSchema.optional(),
    toBlock: blockTagSchema.optional(),
})
    .strict();
const marketplaceMethodCallBaseSchema = z.object({
    chain: supportedChainSlugSchema.or(z.string().min(1)),
    address: addressSchema,
    method: z.string().min(1),
    abiName: z.string().min(1),
    deploymentName: z.string().min(1),
});
/**
 * Decoded outputs are positional — one entry per ABI output, in declaration
 * order, with nested tuples as arrays. Names and types come from the method
 * metadata returned by `listMarketplaceMethods`.
 */
export const marketplaceCallFunctionDataSchema = marketplaceMethodCallBaseSchema
    .extend({
    operation: z.literal(MARKETPLACE_METHOD_OPERATION_CALL),
    abiType: z.literal('function'),
    outputs: z.array(z.unknown()),
})
    .strict();
/**
 * One log from an `eth_simulateV1` call result. Simulation logs often omit or
 * zero out block/tx identity fields, so only address/topics/data are required.
 */
export const marketplaceSimulateCallLogSchema = z
    .object({
    address: addressSchema,
    topics: z.array(z.string().regex(/^0x[0-9a-fA-F]*$/)),
    data: z.string().regex(/^0x[0-9a-fA-F]*$/),
    blockNumber: z.union([z.string(), z.number()]).optional(),
    transactionHash: z.string().optional(),
    transactionIndex: z.union([z.string(), z.number()]).optional(),
    blockHash: z.string().optional(),
    logIndex: z.union([z.string(), z.number()]).optional(),
    removed: z.boolean().optional(),
})
    .strict();
/**
 * Non-view function dry-run via `eth_simulateV1`: decoded `outputs` plus
 * simulation status, gas, and emitted logs.
 */
export const marketplaceSimulateCallFunctionDataSchema = marketplaceMethodCallBaseSchema
    .extend({
    operation: z.literal(MARKETPLACE_METHOD_OPERATION_SIMULATE_CALL),
    abiType: z.literal('function'),
    outputs: z.array(z.unknown()),
    status: z.enum(['0x0', '0x1']),
    gasUsed: z.string().regex(/^0x[0-9a-fA-F]+$/),
    logs: z.array(marketplaceSimulateCallLogSchema),
})
    .strict();
/** @deprecated Prefer marketplaceCallFunctionDataSchema or the method-call union. */
export const marketplaceFunctionCallDataSchema = z.union([
    marketplaceCallFunctionDataSchema,
    marketplaceSimulateCallFunctionDataSchema,
]);
/** One marketplace event log: getLogs fields plus positional decoded `args`. */
export const marketplaceEventLogSchema = logEntrySchema
    .extend({
    args: z.array(z.unknown()),
})
    .strict();
/**
 * Event query result — same pagination envelope as `getLogs`, scoped to one
 * published event on one deployment.
 */
export const marketplaceEventCallDataSchema = marketplaceMethodCallBaseSchema
    .extend({
    operation: z.literal(MARKETPLACE_METHOD_OPERATION_GET_LOGS),
    abiType: z.literal('event'),
    count: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
    cursor: z.union([z.number().int().nonnegative(), z.string().min(1), z.null()]),
    logs: z.array(marketplaceEventLogSchema),
})
    .strict();
export const marketplaceMethodCallDataSchema = z.discriminatedUnion('operation', [
    marketplaceCallFunctionDataSchema,
    marketplaceSimulateCallFunctionDataSchema,
    marketplaceEventCallDataSchema,
]);
